import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';

import {
  ArnoReferenceState,
  IArnoUnresolvedReference,
  useArnoUnresolvedReferences,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { referenceStateChip, splitReferenceKey } from './arnoAdminLabels';
import { ReferenceResolveDialog } from './ReferenceResolveDialog';

/**
 * Bausteinverweise, die im Export kein Ziel haben (#1245).
 *
 * Diese Liste ist kein Fehlerprotokoll, sondern der Arbeitsvorrat: ArnoTop
 * verweist an diesen Stellen auf einen Namen, den der Export nicht mehr
 * hergibt. Der Import korrigiert das bewusst nicht — das ist eine fachliche
 * Entscheidung und steht dem Notariat zu.
 */

export const UnresolvedReferenceTab: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [state, setState] = useState<ArnoReferenceState | ''>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<IArnoUnresolvedReference | null>(
    null
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { unresolvedPage, isFetchingUnresolved } = useArnoUnresolvedReferences({
    page,
    pageSize,
    search,
    state: state || undefined,
  });

  const rows = useMemo(() => unresolvedPage?.rows ?? [], [unresolvedPage]);
  const states = unresolvedPage?.states;

  const columns: GridColDef[] = [
    {
      field: 'referenceKey',
      headerName: 'Verweis in ArnoTop',
      flex: 3,
      minWidth: 280,
      renderCell: parameters => {
        const row = parameters.row as IArnoUnresolvedReference;
        const parts = splitReferenceKey(row.referenceKey);
        return (
          <Box>
            <Typography variant='body2'>{parts.blockName}</Typography>
            <Typography variant='caption' color='text.secondary'>
              {parts.category} · {parts.area}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'owners',
      headerName: 'Steht in',
      flex: 2,
      minWidth: 220,
      sortable: false,
      renderCell: parameters => {
        const row = parameters.row as IArnoUnresolvedReference;
        const [first, ...rest] = row.owners;
        return (
          <Box>
            <Typography variant='body2' noWrap>
              {first ?? '–'}
            </Typography>
            {rest.length > 0 && (
              <Typography variant='caption' color='text.secondary'>
                und {rest.length} weitere
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'occurrences',
      headerName: 'Vorkommen',
      width: 110,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'resolvedBlockKey',
      headerName: 'Zielbaustein',
      flex: 2,
      minWidth: 200,
      renderCell: parameters =>
        parameters.row.resolvedBlockKey ? (
          <Typography variant='body2'>
            {parameters.row.resolvedBlockKey}
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            –
          </Typography>
        ),
    },
    {
      field: 'state',
      headerName: 'Stand',
      width: 190,
      renderCell: parameters => {
        const chip = referenceStateChip(
          (parameters.row as IArnoUnresolvedReference).state
        );
        return <Chip size='small' color={chip.color} label={chip.label} />;
      },
    },
  ];

  return (
    <Box>
      <Alert severity='info' sx={{ mb: 2 }}>
        Offene Verweise werden nie automatisch gesetzt. Wird einer von Hand
        aufgelöst, holt er seinen Baustein ab sofort in die Dokumente, die ihn
        verwenden.
      </Alert>

      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant='body2' sx={{ flexGrow: 1 }}>
          {states
            ? `${states.open} offen · ${states.resolved} aufgelöst · ${states.ignored} bewusst offen gelassen`
            : ' '}
        </Typography>
        <TextField
          size='small'
          label='Suche (Verweis oder Fundstelle)'
          value={searchInput}
          onChange={event => setSearchInput(event.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          size='small'
          select
          label='Stand'
          value={state}
          onChange={event => {
            setState(event.target.value as ArnoReferenceState | '');
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=''>Alle</MenuItem>
          <MenuItem value='open'>Offen</MenuItem>
          <MenuItem value='resolved'>Aufgelöst</MenuItem>
          <MenuItem value='ignored'>Bewusst offen gelassen</MenuItem>
        </TextField>
      </Stack>

      <DataGrid
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        getRowId={row => row.referenceKey}
        loading={isFetchingUnresolved}
        paginationMode='server'
        rowCount={unresolvedPage?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={model => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={parameters =>
          setSelected(parameters.row as IArnoUnresolvedReference)
        }
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      <ReferenceResolveDialog
        reference={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};
